import { camelCase, kebabCase, snakeCase, toString } from 'lodash-es'
import TRANSLATIONS from '~/translations/master'

const TRANSLATIONS_MAP = createTranslationMap(TRANSLATIONS)

const $t: TranslateFn = (
  key: TranslationKey,
  params?: number | TranslationParams,
  plural?: number,
) => {
  return typeof params === 'number'
    ? getTranslation(TRANSLATIONS_MAP, key, {}, params)
    : getTranslation(TRANSLATIONS_MAP, key, params, plural)
}

type FilterActionType =
  | 'check'
  | 'uncheck'
  | 'select'
  | 'select minimum'
  | 'select maximum'

const ROOMS_KEYS = ['bathrooms', 'bedrooms'] as const
type RoomsKey = (typeof ROOMS_KEYS)[number]
const DISTANCE_KEYS = ['sea', 'lake', 'ski', 'center'] as const
type DistanceKey = (typeof DISTANCE_KEYS)[number]
const PRICE_RANGE_KEY = 'priceRange' as const
type PriceRangeKey = typeof PRICE_RANGE_KEY
const STAR_RATING_KEY = 'starRating' as const
type StarRatingKey = typeof STAR_RATING_KEY

function isDistanceKey(key: string): key is DistanceKey {
  return DISTANCE_KEYS.includes(key as DistanceKey)
}

function isRoomsKey(key: string): key is RoomsKey {
  return ROOMS_KEYS.includes(key as RoomsKey)
}

function isPriceRangeKey(key: string): key is PriceRangeKey {
  return key === PRICE_RANGE_KEY
}

function isStarRatingKey(key: string): key is StarRatingKey {
  return key === STAR_RATING_KEY
}

function createTranslationKey(
  value: unknown,
  parentKey: 'search' | 'facets' = 'search',
  key?: string,
): TranslationKey {
  if (parentKey === 'facets') {
    return `www.facets.${key}` as TranslationKey
  }

  if (key && isDistanceKey(key)) {
    return `www.${parentKey}.distances.${value}` as TranslationKey
  }

  const valueKey = camelCase(toString(value))
  const path = key ? `${key}.${valueKey}` : valueKey

  return `www.${parentKey}.${path}` as TranslationKey
}

function getArrayDifference<T>(newArr: T[], oldArr: T[]): T | undefined {
  if (newArr.length > oldArr.length) {
    return newArr.find((val) => !oldArr.includes(val))
  }
  return oldArr.find((val) => !newArr.includes(val))
}

function getActionType(
  filterType: FilterType,
  oldValue: unknown,
  newValue: unknown,
): FilterActionType {
  const isMultiSelect =
    filterType === 'multi' && Array.isArray(newValue) && Array.isArray(oldValue)
  if (isMultiSelect) {
    return newValue.length > oldValue.length ? 'check' : 'uncheck'
  }

  const isRange =
    filterType === 'range' && Array.isArray(oldValue) && Array.isArray(newValue)
  if (isRange) {
    return oldValue[0] !== newValue[0] ? 'select minimum' : 'select maximum'
  }

  return 'select'
}

function getFilterLabel(
  conf: FilterConf,
  oldValue: unknown,
  newValue: unknown,
): string {
  if (
    conf.type === 'range' &&
    Array.isArray(oldValue) &&
    Array.isArray(newValue)
  ) {
    if (oldValue[0] !== newValue[0]) return String(newValue[0])
    if (oldValue[1] !== newValue[1]) {
      return newValue[1] >= 5000 ? `5000+` : String(newValue[1])
    }
    return 'range'
  }

  if (isDistanceKey(conf.key)) {
    return newValue
      ? $t(createTranslationKey(newValue, 'search', conf.key))
      : $t('www.common.any')
  }

  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    const changedValue = getArrayDifference(newValue, oldValue)
    if (changedValue) {
      return $t(createTranslationKey(changedValue, 'search', conf.key))
    }
  }

  if (isStarRatingKey(conf.key)) {
    return newValue
      ? `${newValue}${(newValue as number) < 5 ? '+' : ''}`
      : $t('www.common.any')
  }

  return newValue
    ? $t(createTranslationKey(newValue, 'search', conf.key))
    : $t('www.common.any')
}

function getFilterName(conf: FilterConf): string {
  if (isPriceRangeKey(conf.key)) return 'price-per-stay'
  if (isRoomsKey(conf.key)) return 'rooms'
  if (isDistanceKey(conf.key)) return 'distances'

  return conf.key
}

function getFilterCategory(
  conf: FilterConf,
  newValue: unknown,
  currency: string,
): string {
  if (isPriceRangeKey(conf.key)) return currency
  if (isDistanceKey(conf.key)) {
    return $t(createTranslationKey(newValue, 'facets', conf.key))
  }

  return conf.key
}

function filter(conf: FilterConf, oldValue: unknown, value: unknown): void {
  const { currency } = useConfdata()
  const tracking = useTracking()
  const route = useRoute()
  const { language } = useL10N()

  const event = {
    event: `filter_${snakeCase(getFilterName(conf))}`,
    action: getActionType(conf.type, oldValue, value),
    label: getFilterLabel(conf, oldValue, value),
    category: getFilterCategory(conf, value, currency),
    component: `filter-${kebabCase(getFilterName(conf))}`,
    position: route.path,
    language,
    _clear: true,
  }

  tracking.send(event)
}

function view(open: boolean, name?: string): void {
  const tracking = useTracking()
  const route = useRoute()
  const { language } = useL10N()

  const event = {
    event: `filter_${snakeCase(name)}`,
    action: 'view',
    label: $t(`www.common.${open ? 'viewLess' : 'viewMore'}`),
    category: undefined,
    component: `filter-${kebabCase(name)}`,
    position: route.path,
    language,
    _clear: true,
  }

  tracking.send(event)
}

function sort(sorting: keyof typeof SORT_OPTIONS): void {
  const tracking = useTracking()
  const route = useRoute()
  const { language } = useL10N()

  const event = {
    event: 'srp_sorting',
    action: 'select',
    label: $t(
      `www.components.views.search.area.sorting.${SORT_OPTIONS[sorting]}` as TranslationKey,
    ),
    category: undefined,
    component: 'srp-sorting',
    position: route.path,
    language,
    _clear: true,
  }

  tracking.send(event)
}

function filterMobile(isMap: boolean) {
  const tracking = useTracking()
  const route = useRoute()
  const { language } = useL10N()

  const event = {
    event: 'srp_mobile_filter',
    action: 'open',
    label: 'Filter',
    category: isMap ? 'new srp map' : 'new srp',
    component: 'srp-mobile-filter',
    position: route.path,
    language,
    _clear: true,
  }

  tracking.send(event)
}

export default {
  filter,
  filterMobile,
  sort,
  view,
}
